import React, { useRef, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import PanToolIcon from '@mui/icons-material/PanTool';
import MouseIcon from '@mui/icons-material/Mouse';
import CropFreeIcon from '@mui/icons-material/CropFree';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TimelineIcon from '@mui/icons-material/Timeline';

export type ToolMode = 'cursor' | 'pan' | 'boundingbox' | 'workzone' | 'arrow' | 'delineator';

interface BottomRightToolsProps {
  currentTool: ToolMode;
  onToolChange: (tool: ToolMode) => void;
  onSnapToWorkzone?: () => void;
  onCaptureSnapshot?: () => void;
  onClearWorkzone?: () => void;
  hasWorkzone?: boolean;
}

const buttonStyle = (active: boolean) => ({
  width: 54,
  height: 54,
  backgroundColor: active ? '#E53935' : '#FFFFFF',
  color: active ? '#FFFFFF' : '#111827',
  boxShadow: '0px 14px 30px rgba(15, 23, 42, 0.18)',
  borderRadius: '50%',
  border: active ? 'none' : '1px solid rgba(17, 24, 39, 0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: active ? '#C62828' : '#F3F4F6'
  }
});

// Delay before closing submenu (in ms) - gives user time to move mouse to submenu
const SUBMENU_CLOSE_DELAY = 400;

export const BottomRightTools: React.FC<BottomRightToolsProps> = ({
  currentTool,
  onToolChange,
  onSnapToWorkzone,
  onCaptureSnapshot,
  onClearWorkzone,
  hasWorkzone = false,
}) => {
  const [showWorkzoneMenu, setShowWorkzoneMenu] = React.useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cancel any pending close when mouse enters the menu area
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Start delayed close when mouse leaves the menu area
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowWorkzoneMenu(false);
    }, SUBMENU_CLOSE_DELAY);
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 32,
        top: 'calc(50% - 180px)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      {/* <Tooltip title="Cursor (Select)" placement="left">
        <IconButton
          onClick={() => onToolChange('cursor')}
          sx={buttonStyle(currentTool === 'cursor')}
        >
          <MouseIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Pan" placement="left">
        <IconButton
          onClick={() => onToolChange('pan')}
          sx={buttonStyle(currentTool === 'pan')}
        >
          <PanToolIcon />
        </IconButton>
      </Tooltip> */}

      <Tooltip title="Bounding Box" placement="left">
        <IconButton
          onClick={() => onToolChange('boundingbox')}
          sx={buttonStyle(currentTool === 'boundingbox')}
        >
          <CropFreeIcon />
        </IconButton>
      </Tooltip>

      <Box
        sx={{ position: 'relative', display: 'inline-flex' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Tooltip title="Workzone tools" placement="left">
          <IconButton
            onClick={() => {
              handleMouseEnter(); // Cancel any pending close
              setShowWorkzoneMenu((prev) => !prev);
            }}
            sx={buttonStyle(currentTool === 'workzone')}
          >
            <img
              src="/images/polygon.svg"     
              alt="Workzone tools"
              style={{ width: 38, height: 38 }}
            />
          </IconButton>
        </Tooltip>

        {showWorkzoneMenu && (
          <Box
            sx={{
              position: 'absolute',
              right: 60,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'white',
              borderRadius: 3,
              padding: 1,
              boxShadow: '0px 10px 20px rgba(15, 23, 42, 0.15)'
            }}
          >
            <Tooltip title="Draw Workzone" placement="left">
              <IconButton
                size="small"
                onClick={() => {
              onToolChange('workzone');
              setShowWorkzoneMenu(false);
                }}
                sx={{ width: 40, height: 40, color: '#111827' }}
              >
                <img src="/images/polygon.svg" alt="Draw Workzone" style={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={hasWorkzone ? "Clear Workzone" : "No workzone to clear"} placement="left">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (hasWorkzone) onClearWorkzone?.();
                    setShowWorkzoneMenu(false);
                  }}
                  disabled={!hasWorkzone}
                  sx={{
                    width: 40,
                    height: 40,
                    color: hasWorkzone ? '#DC2626' : '#9CA3AF'
                  }}
                >
                  <img src="/images/eraser.svg" alt="Clear Workzone" style={{ width: 20, height: 20, opacity: hasWorkzone ? 1 : 0.6 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Tooltip title="Arrow" placement="left">
        <IconButton
          onClick={() => onToolChange('arrow')}
          sx={buttonStyle(currentTool === 'arrow')}
        >
          <TimelineIcon />
        </IconButton>
      </Tooltip>

      {/* <Tooltip title="Capture Snapshot" placement="left">
        <IconButton
          onClick={onCaptureSnapshot}
          sx={buttonStyle(false)}
        >
          <CameraAltIcon />
        </IconButton>
      </Tooltip> */}

      <Tooltip title="Snap to Workzone" placement="left">
        <IconButton
          onClick={onSnapToWorkzone}
          sx={buttonStyle(false)}
        >
          <img
            src="/images/snap.svg"     
            alt="Snap"
            style={{ width: 38, height: 38 }}  // match your desired size for snap icon
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
